import React, { useMemo } from 'react';

import { useRecoilValue } from 'recoil';

import { hasModule, inRn, mString, pluralize } from 'lib/helpers/utility';
import { COMPLETED_TRANSACTION_STATUSES } from 'lib/constants/transaction/completedTransactionStatuses';

import { modulesState } from 'components/state';
import Allowed from 'components/shared/Permissions/Allowed';
import { DOC_GAPS } from 'components/shared/DocumentSheet/lib/docStyles';
import DocSummaryRows from 'components/shared/DocumentSheet/components/DocSummaryRows';
import {
  getAmountPaid,
  getNetGratuity,
  getNetSurcharge,
  getSaleAmount
} from 'components/shared/Receipt/lib/receiptAmounts';

import { calculateTotals, calculateWalkoutPaid } from './lib/helpers';

const ReceiptSummary = ({
  transaction,
  transactions,
  isPatient,
  unallocatedAmount = 0,
  walkout = false,
  walkoutTransactions = []
}) => {
  const { id, type, amount_refunded } = transaction || {};

  const isPayer = !!transaction?.payer_id;

  const practiceModules = useRecoilValue(modulesState);
  const hasTaxModule = hasModule(practiceModules, 'medipay.tax');
  const hasTechFeeModule = hasModule(practiceModules, 'medipay.technology_fee');

  const isWriteoff = type === 'writeoff';

  const surchargeAmount = getNetSurcharge(transaction);
  const gratuityAmount = getNetGratuity(transaction);
  const amountPaid = getAmountPaid(transaction);
  const amountTotal = inRn(transaction?.amount_total);
  const amountPaidWithSurcharge = amountPaid + surchargeAmount + gratuityAmount;

  const totals = useMemo(() => calculateTotals(transactions), [transactions]);

  const walkoutPaid = useMemo(
    () => calculateWalkoutPaid(walkoutTransactions),
    [walkoutTransactions]
  );

  const previousPayments = totals.totalAmountPaid - (!isWriteoff ? amountPaid : 0);

  const hasInvoiceOrManyTransactions =
    !!transaction?.invoice || (transactions.length > 0 && transaction?.id !== transactions[0]?.id);

  const amountDue = transactions?.reduce((acc, item) => {
    if (!item.invoice) return acc;

    const { balance = 0 } = item?.invoice || {};

    return acc + balance + (isWriteoff ? amountPaid : 0);
  }, 0);

  const isCompleted = COMPLETED_TRANSACTION_STATUSES.includes(transaction?.status?.toLowerCase());

  const currentAmountDue = hasInvoiceOrManyTransactions
    ? amountDue + amountPaidWithSurcharge
    : totals.totalBalance + amountPaidWithSurcharge;

  const rows = [
    {
      label: 'Subtotal',
      value: mString(
        !hasInvoiceOrManyTransactions ? getSaleAmount(transaction) : totals.totalAmount
      )
    },
    (hasTechFeeModule || isPatient) && surchargeAmount > 0
      ? { label: 'Technology fee', value: mString(surchargeAmount) }
      : null,
    gratuityAmount > 0 ? { label: 'Tip', value: mString(gratuityAmount) } : null,
    amount_refunded > 0 ? { label: 'Refund', value: mString(amount_refunded * -1) } : null,
    ...(hasInvoiceOrManyTransactions
      ? [
          totals?.totalWriteOff > 0 && !isWriteoff
            ? { label: 'Write off', value: mString(totals.totalWriteOff * -1) }
            : null,
          totals?.totalDiscount > 0
            ? { label: 'Discount', value: mString(totals.totalDiscount * -1) }
            : null,
          (hasTaxModule || isPatient) && totals?.totalTax > 0
            ? { label: 'Tax', value: mString(totals.totalTax) }
            : null,
          !walkout && previousPayments > 0
            ? { label: 'Previous payments', value: mString(previousPayments * -1) }
            : null
        ]
      : []),
    !isPayer && currentAmountDue
      ? { label: 'Current amount due', value: mString(currentAmountDue) }
      : null,
    id
      ? {
          label: `Total${walkout || isCompleted ? ' paid' : ''}`,
          value: mString(
            walkout ? walkoutPaid : isCompleted ? amountPaidWithSurcharge : amountTotal
          ),
          strong: true
        }
      : null
  ].filter(Boolean);

  const remainingRows =
    id && !isPayer
      ? [
          totals?.totalBalance > 0
            ? {
                label: `Remaining invoice ${pluralize('balance', transactions?.length)}`,
                value: mString(totals.totalBalance)
              }
            : null,
          unallocatedAmount > 0 && isCompleted
            ? { label: 'Unallocated amount', value: mString(unallocatedAmount) }
            : null
        ].filter(Boolean)
      : [];

  return (
    <Allowed
      requiredPermissions={isPatient ? '' : 'payments.read'}
      requiredModules={isPatient ? '' : 'medipay.payment_suite'}>
      <DocSummaryRows rows={rows} className={DOC_GAPS.totals} />
      <DocSummaryRows rows={remainingRows} className={DOC_GAPS.totals} />
    </Allowed>
  );
};

export default ReceiptSummary;
